// Every call a page makes, scoped to one industry, typed from the API's own
// contract files (apps/api/src/modules/industry/types.ts and
// assistant/types.ts). Pages import from here, never fetch directly, so the
// URL scheme and error handling live in one place.
//
// The return types are the backend's contract types, which the API
// implements, so they are not hand-written guesses. Once the /api/i routes
// are published in apps/api's AppType, the bodies below can move onto the
// typed `api` client in ./client.ts without any page changing.
import type {
  ActionItem,
  DatasetSchema,
  DatasetStatus,
  IndustrySlug,
  IndustrySummary,
  IngestResult,
  Insight,
  Overview,
} from "@dashboard-intelligent/api/src/modules/industry/types";
import type { AssistantStatus, ChatRequest, ChatResponse } from "@dashboard-intelligent/api/src/modules/assistant/types";
import { apiUrl } from "./client";

/** A failed request, with what the server said and when it may be retried. */
export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly body: unknown,
    /** Seconds from a 429's Retry-After header, when present. */
    readonly retryAfter: number | null = null,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  let res: Response;
  try {
    res = await fetch(`${apiUrl}${path}`, init);
  } catch {
    throw new ApiError("Tidak dapat menghubungi server. Periksa koneksi lalu coba lagi.", 0, null);
  }
  const body: unknown = await res.json().catch(() => null);
  if (!res.ok) {
    const serverMessage = (body as { error?: unknown } | null)?.error;
    const retry = Number(res.headers.get("Retry-After"));
    throw new ApiError(
      typeof serverMessage === "string" ? serverMessage : `Permintaan gagal (HTTP ${res.status}).`,
      res.status,
      body,
      Number.isFinite(retry) && retry > 0 ? retry : null,
    );
  }
  return body as T;
}

const tenant = (slug: IndustrySlug, rest: string) => `/api/i/${encodeURIComponent(slug)}${rest}`;

export const endpoints = {
  industries: () => request<IndustrySummary[]>("/api/industries"),
  overview: (slug: IndustrySlug) => request<Overview>(tenant(slug, "/overview")),
  insights: (slug: IndustrySlug) => request<Insight[]>(tenant(slug, "/insights")),
  actions: (slug: IndustrySlug) => request<ActionItem[]>(tenant(slug, "/actions")),
  datasetStatus: (slug: IndustrySlug) => request<DatasetStatus>(tenant(slug, "/dataset/status")),
  datasetSchema: (slug: IndustrySlug) => request<DatasetSchema>(tenant(slug, "/dataset/schema")),
  assistantStatus: (slug: IndustrySlug) => request<AssistantStatus>(tenant(slug, "/assistant/status")),
  chat: (slug: IndustrySlug, body: ChatRequest) =>
    request<ChatResponse>(tenant(slug, "/assistant/chat"), {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(body),
    }),

  /**
   * Upload a dataset file. A file that parses but fails validation comes back
   * as HTTP 422 with a full IngestResult, which is returned here rather than
   * thrown, because the page shows its detail. Every other failure throws.
   * `table` names the single table a .csv or .xlsx replaces (the 13 doc
   * industries); a whole-dataset .json(.gz) or the pmb spreadsheet omits it.
   */
  async upload(slug: IndustrySlug, file: File, table?: string): Promise<IngestResult> {
    const form = new FormData();
    form.append("file", file);
    if (table) form.append("table", table);
    try {
      return await request<IngestResult>(tenant(slug, "/dataset/upload"), { method: "POST", body: form });
    } catch (err) {
      if (err instanceof ApiError && err.status === 422 && err.body && typeof err.body === "object" && "ok" in err.body) {
        return err.body as IngestResult;
      }
      throw err;
    }
  },
};
